import React, { useEffect, useMemo, useState } from 'react';
import { Input, Select, DatePicker, Button, InputNumber } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useFilterContainerClassName, useFilterInputContainerClassName } from '../../../../utils/languageUtils';
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';
import { SystemOperationLogFiltersType } from '../graphql/queries/getSystemOperationLogListing';

interface OperationLogFilterProps {
    filters: SystemOperationLogFiltersType;
    onFilterChange: (filters: SystemOperationLogFiltersType) => void;
    onReset: () => void;
}

type DraftFilters = {
    bizType?: number;
    bizId?: string;
    operationType?: number;
    operatorId?: number;
    operatedAtRange?: [Dayjs | null, Dayjs | null] | null;
};

const toDraftFilters = (filters: SystemOperationLogFiltersType): DraftFilters => ({
    bizType: filters.bizType,
    bizId: filters.bizId,
    operationType: filters.operationType,
    operatorId: filters.operatorId,
    operatedAtRange:
        filters.operatedAtStart && filters.operatedAtEnd
            ? [dayjs.unix(filters.operatedAtStart), dayjs.unix(filters.operatedAtEnd)]
            : null,
});

const toAppliedFilters = (draft: DraftFilters): SystemOperationLogFiltersType => ({
    bizType: draft.bizType,
    bizId: draft.bizId?.trim() || undefined,
    operationType: draft.operationType,
    operatorId: draft.operatorId,
    operatedAtStart: draft.operatedAtRange?.[0]?.unix(),
    operatedAtEnd: draft.operatedAtRange?.[1]?.unix(),
});

const OperationLogFilter: React.FC<OperationLogFilterProps> = ({
    filters,
    onFilterChange,
    onReset,
}) => {
    const { t } = useTranslation();
    const containerClassName = useFilterContainerClassName();
    const inputContainerClassName = useFilterInputContainerClassName();
    const [draftFilters, setDraftFilters] = useState<DraftFilters>(() => toDraftFilters(filters));

    useEffect(() => {
        setDraftFilters(toDraftFilters(filters));
    }, [filters]);

    const bizTypeOptions = useMemo(
        () => [
            { value: 1, label: t('central.operationLogs.bizTypes.zipcode') },
            { value: 2, label: t('central.operationLogs.bizTypes.hubDeliverableZipcode') },
            { value: 3, label: t('central.operationLogs.bizTypes.hubPickupZipcode') },
            { value: 4, label: t('central.operationLogs.bizTypes.defaultZipcodePickupHub') },
            { value: 5, label: t('central.operationLogs.bizTypes.commonConfig') },
            { value: 6, label: t('central.operationLogs.bizTypes.warehousePlanningEntryConfig') },
            { value: 7, label: t('central.operationLogs.bizTypes.addressLocationReview') },
            { value: 8, label: t('central.operationLogs.bizTypes.setDeliveredStatus') },
            { value: 9, label: t('central.operationLogs.bizTypes.standardizedAddressManagement') },
            { value: 10, label: t('central.operationLogs.bizTypes.account') },
            { value: 11, label: t('central.operationLogs.bizTypes.dsp') },
            { value: 13, label: t('central.operationLogs.bizTypes.accountWarehouseOperator') },
            { value: 14, label: t('central.operationLogs.bizTypes.abAddressMapping') },
            { value: 15, label: t('central.operationLogs.bizTypes.whsWorkbench') },
            { value: 16, label: t('central.operationLogs.bizTypes.driverLearningCenterFaq') },
        ],
        [t]
    );

    const operationTypeOptions = useMemo(
        () => [
            { value: 1, label: t('central.operationLogs.operationTypes.create') },
            { value: 2, label: t('central.operationLogs.operationTypes.update') },
            { value: 3, label: t('central.operationLogs.operationTypes.delete') },
            { value: 4, label: t('central.operationLogs.operationTypes.batchUpdate') },
            { value: 5, label: t('central.operationLogs.operationTypes.import') },
            { value: 6, label: t('central.operationLogs.operationTypes.export') },
        ],
        [t]
    );

    const labelBizType = String(t('central.operationLogs.filters.bizType'));
    const labelBizId = String(t('central.operationLogs.filters.bizId'));
    const labelOperationType = String(t('central.operationLogs.filters.operationType'));
    const labelOperatorId = String(t('central.operationLogs.filters.operatorId'));
    const labelOperatedAt = String(t('central.operationLogs.filters.operatedAt'));
    const colLabels = [labelBizType, labelBizId, labelOperationType, labelOperatorId, labelOperatedAt];

    const handleFilter = () => {
        onFilterChange(toAppliedFilters(draftFilters));
    };

    const handleReset = () => {
        setDraftFilters({});
        onReset();
    };

    return (
        <div className="mb-2 pl-0 py-2">
            <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                <div className="min-w-0">
                    <div className={containerClassName}>
                        <span className={getSmartLabelClassName(colLabels)}>{labelBizType}</span>
                        <div className={inputContainerClassName}>
                            <Select
                                value={draftFilters.bizType}
                                onChange={(value) => setDraftFilters((prev) => ({ ...prev, bizType: value }))}
                                placeholder={t('central.operationLogs.filters.bizTypePlaceholder') as string}
                                allowClear
                                options={bizTypeOptions}
                                className="w-full"
                            />
                        </div>
                    </div>
                </div>
                <div className="min-w-0">
                    <div className={containerClassName}>
                        <span className={getSmartLabelClassName(colLabels)}>{labelBizId}</span>
                        <div className={inputContainerClassName}>
                            <Input
                                value={draftFilters.bizId}
                                onChange={(e) => setDraftFilters((prev) => ({ ...prev, bizId: e.target.value }))}
                                placeholder={t('central.operationLogs.filters.bizIdPlaceholder') as string}
                                allowClear
                                className="w-full"
                            />
                        </div>
                    </div>
                </div>
                <div className="min-w-0">
                    <div className={containerClassName}>
                        <span className={getSmartLabelClassName(colLabels)}>{labelOperationType}</span>
                        <div className={inputContainerClassName}>
                            <Select
                                value={draftFilters.operationType}
                                onChange={(value) => setDraftFilters((prev) => ({ ...prev, operationType: value }))}
                                placeholder={t('central.operationLogs.filters.operationTypePlaceholder') as string}
                                allowClear
                                options={operationTypeOptions}
                                className="w-full"
                            />
                        </div>
                    </div>
                </div>
                <div className="min-w-0">
                    <div className={containerClassName}>
                        <span className={getSmartLabelClassName(colLabels)}>{labelOperatorId}</span>
                        <div className={inputContainerClassName}>
                            <InputNumber
                                value={draftFilters.operatorId}
                                onChange={(value) => setDraftFilters((prev) => ({ ...prev, operatorId: value ?? undefined }))}
                                placeholder={t('central.operationLogs.filters.operatorIdPlaceholder') as string}
                                className="w-full"
                            />
                        </div>
                    </div>
                </div>
                <div className="min-w-0 col-span-2">
                    <div className={containerClassName}>
                        <span className={getSmartLabelClassName(colLabels)}>{labelOperatedAt}</span>
                        <div className={inputContainerClassName}>
                            <DatePicker.RangePicker
                                value={draftFilters.operatedAtRange}
                                onChange={(dates) =>
                                    setDraftFilters((prev) => ({
                                        ...prev,
                                        operatedAtRange: dates as [Dayjs | null, Dayjs | null] | null,
                                    }))
                                }
                                showTime
                                format="YYYY-MM-DD HH:mm:ss"
                                allowClear
                                className="w-full"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
                <Button type="primary" onClick={handleFilter} className="w-auto min-w-24">
                    <span className="font-bold">{t('components.button.filter')}</span>
                </Button>
                <Button onClick={handleReset} className="w-auto min-w-24">
                    <span className="font-bold">{t('components.button.reset')}</span>
                </Button>
            </div>
        </div>
    );
};

export default OperationLogFilter;
